import { useEffect, useState } from "react";
import { Plus, Save, Trash2, Loader2, ArrowLeft, Eye, EyeOff, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ImageField from "../components/ImageField";
import RichEditor from "../components/RichEditor";

const BLANK = {
  title: "", slug: "", excerpt: "", content: "", category: "সাধারণ",
  tags: [], featured_image: "", author: "শ্রুতি", status: "published",
  seoTitle: "", seoDescription: "",
};

export default function BlogManager() {
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/list/blog").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.title) { toast.error("শিরোনাম দিন"); return; }
    setBusy(true);
    try {
      const payload = { ...editing, tags: Array.isArray(editing.tags) ? editing.tags : String(editing.tags || "").split(",").map((t) => t.trim()).filter(Boolean) };
      if (editing.id) await api.put(`/admin/list/blog/${editing.id}`, payload);
      else await api.post("/admin/list/blog", payload);
      toast.success("সংরক্ষিত হয়েছে");
      setEditing(null);
      load();
    } catch { toast.error("ব্যর্থ"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("মুছে ফেলবেন?")) return;
    await api.delete(`/admin/list/blog/${id}`);
    load();
  };

  if (editing) {
    const set = (k, v) => setEditing({ ...editing, [k]: v });
    return (
      <div className="max-w-3xl">
        <button onClick={() => setEditing(null)} className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 mb-4"><ArrowLeft className="h-4 w-4" /> সব পোস্ট</button>
        <div className="flex items-center justify-between mb-5">
          <h1 className="font-heading text-2xl font-bold text-stone-800">{editing.id ? "পোস্ট সম্পাদনা" : "নতুন পোস্ট"}</h1>
          <button onClick={save} disabled={busy} data-testid="blog-save" className="inline-flex items-center gap-2 rounded-full bg-[var(--c-primary)] text-white px-5 py-2 text-sm font-medium hover:bg-[var(--c-secondary)] transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} প্রকাশ / সংরক্ষণ
          </button>
        </div>
        <div className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4">
          <div><label className="block text-sm font-medium text-stone-600 mb-1.5">শিরোনাম</label><input value={editing.title} onChange={(e) => set("title", e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-stone-600 mb-1.5">স্লাগ (ঐচ্ছিক)</label><input value={editing.slug} onChange={(e) => set("slug", e.target.value)} placeholder="স্বয়ংক্রিয়" className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" /></div>
            <div><label className="block text-sm font-medium text-stone-600 mb-1.5">বিভাগ</label><input value={editing.category} onChange={(e) => set("category", e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" /></div>
            <div><label className="block text-sm font-medium text-stone-600 mb-1.5">লেখক</label><input value={editing.author} onChange={(e) => set("author", e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" /></div>
            <div><label className="block text-sm font-medium text-stone-600 mb-1.5">ট্যাগ (কমা দিয়ে)</label><input value={Array.isArray(editing.tags) ? editing.tags.join(", ") : editing.tags} onChange={(e) => set("tags", e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" /></div>
          </div>
          <ImageField label="ফিচার্ড ছবি" value={editing.featured_image} onChange={(v) => set("featured_image", v)} folder="uploads" />
          <div><label className="block text-sm font-medium text-stone-600 mb-1.5">সংক্ষিপ্তসার</label><textarea value={editing.excerpt} onChange={(e) => set("excerpt", e.target.value)} rows={2} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm resize-y" /></div>
          <div><label className="block text-sm font-medium text-stone-600 mb-1.5">মূল লেখা</label><RichEditor value={editing.content} onChange={(v) => set("content", v)} /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-stone-600 mb-1.5">SEO টাইটেল</label><input value={editing.seoTitle} onChange={(e) => set("seoTitle", e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" /></div>
            <div><label className="block text-sm font-medium text-stone-600 mb-1.5">স্ট্যাটাস</label>
              <select value={editing.status} onChange={(e) => set("status", e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm">
                <option value="published">প্রকাশিত</option><option value="draft">খসড়া</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-heading text-2xl font-bold text-stone-800">ব্লগ</h1><p className="text-sm text-stone-500 mt-1">লেখা তৈরি ও প্রকাশ করুন</p></div>
        <button onClick={() => setEditing({ ...BLANK })} data-testid="add-blog" className="inline-flex items-center gap-2 rounded-full bg-[var(--c-primary)] text-white px-5 py-2 text-sm font-medium hover:bg-[var(--c-secondary)] transition-colors"><Plus className="h-4 w-4" /> নতুন পোস্ট</button>
      </div>
      {posts == null ? <div className="text-stone-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> লোড হচ্ছে...</div>
        : posts.length === 0 ? <p className="text-stone-400">কোনো পোস্ট নেই।</p>
        : (
          <div className="space-y-3">
            {posts.map((p) => (
              <div key={p.id} className="bg-white rounded-2xl border border-stone-200 p-4 flex items-center gap-4">
                <img src={p.featured_image} alt={p.title} className="h-16 w-24 object-cover rounded-lg bg-stone-100" />
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold text-stone-800 truncate">{p.title}</div>
                  <div className="text-xs text-stone-400 mt-1">{p.category} · {p.author}</div>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-xs ${p.status === "draft" ? "bg-amber-50 text-amber-600" : "bg-green-50 text-green-600"}`}>{p.status === "draft" ? <EyeOff className="h-3.5 w-3.5 inline" /> : <Eye className="h-3.5 w-3.5 inline" />} {p.status === "draft" ? "খসড়া" : "প্রকাশিত"}</span>
                <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 text-stone-400 hover:text-stone-700"><ExternalLink className="h-4 w-4" /></a>
                <button onClick={() => setEditing({ ...p })} className="rounded-lg bg-stone-100 hover:bg-stone-200 px-3 py-1.5 text-sm text-stone-700">সম্পাদনা</button>
                <button onClick={() => remove(p.id)} className="p-2 text-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
